import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import { Box, EmptyState, Pagination, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import FilterBar from 'components/FilterBar/FilterBar'
import { PageHeader } from 'components/header/PageHeader'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApiPaginationWithPaymentsList } from 'hooks/useApiPaginationWithPaymentsList'
import { useMappedRoles } from 'hooks/useMappedRoles'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { PaymentsResponse, PaymentStatus, PaymentTag } from '../../types/payments'
import SearchFields from './components/Form/SearchFields'
import { CryptoPaymentsHeaderButtons } from './components/Header/CryptoPaymentsHeaderButtons'
import PaymentTable from './components/Table/PaymentTable'
import usePaymentsApi from './hooks/usePaymentsApi'

const Payments = () => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()?.payments
  const [withContentExpanded, setWithContentExpanded] = useState(false)

  const canEdit = permissions?.canEdit
  const canCreatePayIn = permissions?.canCreatePayIn

  const {
    executor,
    isLoading,
    isError,
    data: {
      paymentsList: { transactions, paging, pagingMax },
      searchByRef,
      searchByTransactionHash,
      isExportingCSV,
      dateValue,
      status,
      tag,
    },
    actions: {
      onDateSelected,
      handleExportClicked,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      showSearchResult,
      onResetAll,
      setPaymentsList,
      onPageChange,
      onSearchByRefChanged,
      onSearchByTransactionHash,
      resetSearch,
    },
  } = usePaymentsApi()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useApiPaginationWithPaymentsList({
      onPageChange,
      setPaymentsList,
      executor,
      pagingMax,
    })

  const urlParams = new URLSearchParams(window.location.search)
  const queryPaymentsLinkBy = urlParams.get('dlt-reference') ?? null

  useEffect(() => {
    if (queryPaymentsLinkBy && !isLoading) {
      setWithContentExpanded(true)
      onSearchByRefChanged(queryPaymentsLinkBy)
    }
  }, [])

  const shouldShowNoSearchResults =
    (searchByRef ||
      searchByTransactionHash ||
      status !== PaymentStatus.ALL ||
      tag !== PaymentTag.ALL) &&
    !isLoading &&
    !transactions?.length

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.PAYMENTS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const getPageHeader = () => {
    return t('payments.headerDescriptionTravelRuleV2')
  }

  return (
    <Wrapper className="payments-container">
      {(canEdit || canCreatePayIn) && (
        <PageHeader
          description={getPageHeader()}
          renderRightSection={<CryptoPaymentsHeaderButtons refetch={executor} />}
        />
      )}

      <FilterBar
        eventHandlers={{
          executor,
          onMerchantSelected,
          onDateFilterChange: onDateSelected,
          onPaymentStatusChanged,
          handleExportClicked,
          onResetAll,
          showSearchResult,
          onPaymentTagChange,
        }}
        searchQuery={searchByRef || searchByTransactionHash || queryPaymentsLinkBy}
        onSearchQueryReset={() => resetSearch()}
        conditions={{ isExportingCSV }}
        filterDateRange={dateValue}
      >
        <SearchFields
          t={t}
          searchByRef={searchByRef}
          searchByTransactionHash={searchByTransactionHash}
          onSearchByRefChanged={onSearchByRefChanged}
          onSearchByTransactionHash={onSearchByTransactionHash}
          queryPaymentsLinkBy={queryPaymentsLinkBy}
        />
      </FilterBar>

      {!searchByRef &&
        !searchByTransactionHash &&
        !transactions?.length &&
        status === PaymentStatus.ALL &&
        tag === PaymentTag.ALL &&
        isLoading &&
        !isError && (
          <Box paddingT={20}>
            <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
          </Box>
        )}

      {!searchByRef &&
        !searchByTransactionHash &&
        !transactions?.length &&
        status === PaymentStatus.ALL &&
        tag === PaymentTag.ALL &&
        (!isLoading || isError) && (
          <EmptyState
            testId="empty-state"
            LottiePlayer={<Lottie src="no-data" />}
            title={t('payments.emptyStateHeadline')}
            description={t('payments.emptyStateCryptoPaymentsDescription')}
            PrimaryButton={
              (canEdit || canCreatePayIn) && <CryptoPaymentsHeaderButtons refetch={executor} />
            }
          />
        )}

      {(searchByRef ||
        searchByTransactionHash ||
        !!transactions?.length ||
        status !== PaymentStatus.ALL ||
        tag !== PaymentTag.ALL) && (
        <>
          {isError && (
            <EmptyState
              testId="empty-state"
              LottiePlayer={<Lottie src="connection-error" />}
              title={t('defaultError')}
            />
          )}

          {shouldShowNoSearchResults && (
            <NoSearchResults
              testIds={{
                headline: 'payment-links-search-status-card',
                description: 'payment-links-no-result-label',
                clearButton: 'payment-links-clear-result-btn',
              }}
              clearSearchCriteria={onResetAll}
            />
          )}

          {!!transactions?.length && !isError && (
            <PaymentTable
              transactions={transactions as unknown as PaymentsResponse[]}
              isLoading={isLoading}
            />
          )}
          {!isError && !!paging?.total && (
            <Pagination
              testId="payments-pagination"
              page={page}
              rowsPerPage={rowsPerPage}
              total={paging.total}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </Wrapper>
  )
}

export default React.memo(Payments)
